import { inject, Injectable } from '@angular/core';
import { APP_ENVIRONMENT, APP_VERSION, ConfigSettingsService, TelemetryService } from '@ebeihilfe/core';
import { ModalService } from '@ebeihilfe/web-logic';
import { TranslateService } from '@ngx-translate/core';
import { browserTracingIntegration, ErrorEvent, EventHint, init } from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private readonly configService = inject(ConfigSettingsService);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly modalService = inject(ModalService);
  private readonly telemetryService = inject(TelemetryService);
  private readonly translate = inject(TranslateService);
  private readonly versionToken = inject(APP_VERSION);

  private readonly domainRegex = /^https?:\/\/([a-z0-9]+(?:\.[a-z0-9]+)*\.[a-z]+\.[a-z]+)\/res$/;
  private isInitialized = false;

  public async init(): Promise<void> {
    const userHasEnabled = await this.telemetryService.userHasTelemetryEnabled();
    const sentrySettings = this.configService.appSettings.sentry;

    if (sentrySettings && sentrySettings.dsn && !this.isInitialized && userHasEnabled) {
      init({
        beforeSend: this.beforeSend.bind(this),
        dsn: sentrySettings.dsn,
        environment: this.getEnvironment(),
        integrations: [browserTracingIntegration()],
        release: this.versionToken.version + '-' + this.versionToken.buildNumber,
        replaysSessionSampleRate: sentrySettings.replaysSessionSampleRate,
        replaysOnErrorSampleRate: sentrySettings.replaysOnErrorSampleRate,
        tracesSampleRate: sentrySettings.tracesSampleRate,
        tracePropagationTargets: ['localhost'],
      });
      this.isInitialized = true;
    }
  }

  private async beforeSend(event: ErrorEvent, hint: EventHint): Promise<ErrorEvent | null> {
    const userHasEnabled = await this.telemetryService.userHasTelemetryEnabled();

    if (!userHasEnabled || !event.exception || !event.event_id || !hint.originalException) {
      return null;
    }
    const result = await this.modalService.openConfirmationModal({
      content: this.translate.instant('general.errorhandler.modal.header'),
    });
    return result ? event : null;
  }

  private getEnvironment(): string {
    const customer = this.configService.appSettings.connection.res.match(this.domainRegex);
    const customerId = customer ? customer[1] : 'unknown';
    const env = this.environment.production ? 'production' : 'development';
    return customerId + '-' + env;
  }
}
